<script setup lang="ts">
  // V2 order modal for the unified history (both /orders and /pos/history):
  // the shared view tree in interactive mode — NOT preview: permission safety
  // is per-action (money pane self-gates with canManageOrders, the stepper
  // hides its buttons without OrdersManage, the footer gates per-button), so
  // OrdersHistoric-only users keep invoicing, PIN cancel and printing exactly
  // like the legacy ModalPosViewOrder. Order resolution mirrors the preview
  // modal: prefer the live ordersStore instance, fall back to a fetched
  // snapshot (post-mutation refreshes keep that snapshot current).
  import { computed, onBeforeMount, ref, watch } from 'vue';
  import { IOrder } from '@/interfaces/IOrder';
  import OrdersService from '@/api/services/orders.service';
  import { useModal } from '@/composition/use-modal';
  import { useOrdersStore } from '@/stores/orders.store';
  import { useRestaurantStore } from '@/stores/restaurant.store';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosV2ViewWorkspaceBase from '@/components/pos-v2/view/PosV2ViewWorkspaceBase.vue';
  import PosV2OrderModalActions from '@/components/pos-v2/modals/PosV2OrderModalActions.vue';

  const props = defineProps<{
    orderId: number;
  }>();

  const MODAL_NAME = 'pos-v2-order-modal';

  const modals = useModal();
  const ordersStore = useOrdersStore();
  const restaurantStore = useRestaurantStore();

  const storeOrder = computed<IOrder | null>(() => {
    const orders = ordersStore.orders;
    if (!orders) return null;
    return orders.find((o) => o.id === props.orderId) ?? null;
  });

  const providedOrder = ref<IOrder | null>(null);
  const loading = ref(false);

  watch(
    storeOrder,
    (o) => {
      if (o) providedOrder.value = o;
    },
    { immediate: true }
  );

  onBeforeMount(async () => {
    if (providedOrder.value) return;

    const restaurantId = restaurantStore.restaurant?.id;
    if (!restaurantId) return;

    loading.value = true;
    try {
      const fetched = await OrdersService.v2GetOrderById(
        restaurantId,
        props.orderId
      );
      // The store instance may have arrived (socket) while fetching; it wins.
      if (!providedOrder.value) providedOrder.value = fetched;
    } catch (e) {
      console.error('Error fetching order for modal', e);
    } finally {
      loading.value = false;
    }
  });

  function handleClose() {
    modals.hide(MODAL_NAME);
  }
</script>

<template>
  <div class="pos-v2-order-modal">
    <div class="modal-header">
      <h2 class="modal-title">{{ $t('posV2.orderModalTitle') }}</h2>
      <PosUiButton
        icon="close"
        color="neutral"
        variant="ghost"
        size="md"
        @click="handleClose"
      />
    </div>

    <div class="modal-body">
      <PosV2ViewWorkspaceBase
        :order="providedOrder"
        :loading="loading"
        mode="interactive"
      >
        <template #footer>
          <PosV2OrderModalActions />
        </template>
      </PosV2ViewWorkspaceBase>
    </div>
  </div>
</template>

<style scoped lang="scss">
  // Root lives inside .vm--modal.dialog--height-limit (a max-height flex
  // parent): the body asks for 76vh and shrinks on short screens.
  .pos-v2-order-modal {
    @apply tw-flex tw-max-h-full tw-min-h-0 tw-w-full tw-flex-col;

    .modal-header {
      @apply tw-flex tw-flex-none tw-items-center tw-justify-between tw-gap-3 tw-border-b tw-border-neutral-200 tw-px-4 tw-py-3;

      .modal-title {
        @apply tw-text-lg tw-font-bold tw-text-neutral-900;
      }
    }

    .modal-body {
      @apply tw-flex tw-min-h-0 tw-flex-col tw-overflow-hidden;
      height: 76vh;
    }
  }
</style>
